import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { MediaType } from "@utils/constants";
import { VDBIcons } from "@utils/VDBIcons";
import { IconsVariant } from "@components/icons/VDBIcon";
import { ImageSourcePropType } from "react-native";

export const DEFAULT_CERT_ICON_NAME = VDBIcons.REPORT_OLD;

export type LabIconItem = {
  cert_type?: string;
  icon?: {
    data?: string;
    type?: "font" | "url";
    color?: string;
    size?: number;
  };
};

export type LabIconConfig =
  | {
      enabled?: boolean;
      items?: Record<string, LabIconItem>;
    }
  | Record<string, LabIconItem>;

export type CertificateIconProps = {
  name: string;
  variant: IconsVariant;
  uri?: string;
  color?: string;
  source?: ImageSourcePropType;
};

export const LAB_ICON_CONFIG_DUMMY: Record<string, LabIconItem> = {
  gia: {
    cert_type: "gia",
    icon: { data: "gia", type: "font", color: "#1b1f2a" },
  },
  igi: {
    cert_type: "igi",
    icon: { data: "https://example.com/igi.svg", type: "url" },
  },
};

const LAB_CERT_ICON_ASSETS: Record<string, ImageSourcePropType> = {
  gia: require("assets/lab-cert/GIA.png"),
  igi: require("assets/lab-cert/IGI.png"),
  hrd: require("assets/lab-cert/HRD.png"),
  gcal: require("assets/lab-cert/GCAL.png"),
};

const normalizeLabId = (labId?: string) => {
  if (!labId) return "";
  return String(labId).trim().toLowerCase();
};

const isCertificateType = (mediaType?: string) => {
  if (!mediaType) return false;
  const normalizedType = String(mediaType).toLowerCase();
  return normalizedType === MediaType.PDF || normalizedType === "certificate";
};

const normalizeLabIconConfig = (labIconConfig?: LabIconConfig) => {
  const isConfigObject =
    typeof labIconConfig === "object" && labIconConfig !== null;
  const enabled =
    isConfigObject && typeof (labIconConfig as any).enabled === "boolean"
      ? (labIconConfig as any).enabled
      : false;
  const source =
    isConfigObject && typeof (labIconConfig as any).items === "object"
      ? (labIconConfig as any).items
      : labIconConfig;

  const entries =
    typeof source === "object" && source !== null
      ? (source as Record<string, LabIconItem>)
      : {};

  const map: Record<string, LabIconItem> = {};
  Object.entries(entries).forEach(([key, entry]) => {
    if (!entry || typeof entry !== "object") return;
    const certType = normalizeLabId(entry.cert_type || key);
    if (certType) {
      map[certType] = entry;
    }
  });
  return { enabled, map };
};

export const resolveCertificateIconProps = ({
  mediaType,
  labId,
  labIconConfig,
  fallbackIcon = DEFAULT_CERT_ICON_NAME,
  fallbackColor,
}: {
  mediaType?: string;
  labId?: string;
  labIconConfig?: LabIconConfig;
  fallbackIcon?: string;
  fallbackColor?: string;
}): CertificateIconProps => {
  if (!isCertificateType(mediaType)) {
    return { name: fallbackIcon, variant: IconsVariant.VDBCustom };
  }

  const { enabled, map } = normalizeLabIconConfig(labIconConfig);

  if (!enabled) {
    return {
      name: fallbackIcon,
      variant: IconsVariant.VDBCustom,
      color: fallbackColor,
    };
  }

  const normalizedLabId = normalizeLabId(labId);
  if (!normalizedLabId) {
    return {
      name: fallbackIcon,
      variant: IconsVariant.VDBCustom,
      color: fallbackColor,
    };
  }

  const labEntry = map[normalizedLabId];
  const localAsset = labEntry
    ? LAB_CERT_ICON_ASSETS[normalizedLabId]
    : undefined;
  const iconType = labEntry?.icon?.type;
  const iconData = labEntry?.icon?.data;
  const iconColor = labEntry?.icon?.color;

  if (localAsset) {
    return {
      name: fallbackIcon,
      variant: IconsVariant.VDBCustom,
      color: iconColor,
      source: localAsset,
    };
  }

  if (!iconData) {
    return {
      name: fallbackIcon,
      variant: IconsVariant.VDBCustom,
      color: fallbackColor,
    };
  }

  if (iconType === "url") {
    return {
      name: fallbackIcon,
      uri: iconData,
      variant: IconsVariant.CustomUrl,
      color: iconColor,
    };
  }

  return {
    name: fallbackIcon,
    variant: IconsVariant.VDBCustom,
    color: fallbackColor,
  };
};

export const useCertificateIconResolver = () => {
  const labIconConfig = useSelector(
    (state: any) => state.globalData?.lab_icons
  ) as LabIconConfig | undefined;
  const normalizedConfig = useMemo(
    () => normalizeLabIconConfig(labIconConfig as LabIconConfig),
    [labIconConfig]
  );

  return useCallback(
    (
      mediaType?: string,
      labId?: string,
      fallbackIcon?: string,
      fallbackColor?: string
    ) => {
      if (!isCertificateType(mediaType)) {
        return {
          name: fallbackIcon ?? DEFAULT_CERT_ICON_NAME,
          variant: IconsVariant.VDBCustom,
          color: fallbackColor,
        };
      }

      if (!normalizedConfig.enabled) {
        return {
          name: fallbackIcon ?? DEFAULT_CERT_ICON_NAME,
          variant: IconsVariant.VDBCustom,
          color: fallbackColor,
        };
      }

      const normalizedLabId = normalizeLabId(labId);
      if (!normalizedLabId) {
        return {
          name: fallbackIcon ?? DEFAULT_CERT_ICON_NAME,
          variant: IconsVariant.VDBCustom,
          color: fallbackColor,
        };
      }

      const labEntry = normalizedConfig.map[normalizedLabId];
      const iconType = labEntry?.icon?.type;
      const iconData = labEntry?.icon?.data;
      const iconColor = labEntry?.icon?.color;
      const iconSize = labEntry?.icon?.size;
      const localAsset =
        iconType === "url"
          ? undefined
          : labEntry
          ? LAB_CERT_ICON_ASSETS[normalizedLabId]
          : undefined;

      if (localAsset) {
        return {
          name: fallbackIcon ?? DEFAULT_CERT_ICON_NAME,
          variant: IconsVariant.VDBCustom,
          color: iconColor,
          source: localAsset,
        };
      }

      if (!iconData) {
        return {
          name: fallbackIcon ?? DEFAULT_CERT_ICON_NAME,
          variant: IconsVariant.VDBCustom,
          color: fallbackColor,
        };
      }

      if (iconType === "url") {
        return {
          name: fallbackIcon ?? DEFAULT_CERT_ICON_NAME,
          source: iconData,
          variant: IconsVariant.CustomUrl,
          color: iconColor,
          size: iconSize,
        };
      }

      return {
        name: fallbackIcon ?? DEFAULT_CERT_ICON_NAME,
        variant: IconsVariant.VDBCustom,
        color: fallbackColor,
      };
    },
    [normalizedConfig]
  );
};
